import type { QueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { STOCK_ITEM_OPTIONS_QUERY_KEY, fetchStockItemOptions } from '@/hooks/useStockItemOptions'
import { costSaveOutcome, type StockItemRef } from '@/lib/recipeEditor'
import { toastHelpers } from '@/lib/toast-helpers'

/** Query key prefix for one product's recipe (GET /admin/products/:id/recipe). */
export const PRODUCT_RECIPE_QK = 'product-recipe'

/**
 * After an ingredient's cost changes (inline "Set cost"): every surface that
 * shows a recipe cost must re-read it — the picker's rates, the list's status
 * and cost columns, open recipes, option recipes, and Inventory (audit U-10).
 */
export function invalidateAfterCostChange(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: STOCK_ITEM_OPTIONS_QUERY_KEY })
  qc.invalidateQueries({ queryKey: ['admin-products', 'costing', 'health'] })
  qc.invalidateQueries({ queryKey: [PRODUCT_RECIPE_QK] })
  qc.invalidateQueries({ queryKey: ['option-recipe'] })
  qc.invalidateQueries({ queryKey: ['stockItems'] })
}

/** After a recipe or a product's cost is saved, from the sheet or the option dialog. */
export function invalidateAfterRecipeSave(qc: QueryClient, productId?: string): void {
  qc.invalidateQueries({ queryKey: [PRODUCT_RECIPE_QK] })
  qc.invalidateQueries({ queryKey: ['admin-products'] })
  qc.invalidateQueries({ queryKey: ['products'] })
  if (productId) qc.invalidateQueries({ queryKey: ['product-stock-components', productId] })
  qc.invalidateQueries({ queryKey: ['option-recipe'] })
  qc.invalidateQueries({ queryKey: ['menu-modifiers'] })
  qc.invalidateQueries({ queryKey: ['reports-overview'] })
  qc.invalidateQueries({ queryKey: ['stockItems'] })
  qc.invalidateQueries({ queryKey: ['stock-item-used-in'] })
}

/**
 * Set one ingredient's cost from a recipe (the sheet or the option dialog) and
 * report what really happened (walkthrough W-1): the stock list is re-read and,
 * if recipes still price at a receipt average, the toast says so instead of
 * "Cost set". Resolves true when the write succeeded.
 */
export async function saveIngredientCost(
  qc: QueryClient,
  item: StockItemRef,
  cost: number,
  formatMoney: (n: number) => string,
  beforeInvalidate?: () => void,
): Promise<boolean> {
  try {
    await apiClient.updateStockItemCostViaAdmin(item.id, cost)
  } catch (e) {
    toastHelpers.apiError('Set cost', e)
    return false
  }
  beforeInvalidate?.()
  invalidateAfterCostChange(qc)
  let effective: number | null | undefined
  try {
    const list = await qc.fetchQuery({ queryKey: STOCK_ITEM_OPTIONS_QUERY_KEY, queryFn: fetchStockItemOptions })
    effective = list.find((o) => o.id === item.id)?.unit_cost
  } catch {
    effective = undefined
  }
  const outcome = costSaveOutcome(cost, effective)
  if (outcome === 'average_kept' && effective != null) {
    toastHelpers.warning(
      'Saved as last paid only',
      `Recipes still use ${item.name}'s average of ${formatMoney(effective)} per ${item.unit} from stock received. Correct that receipt in Inventory to change it.`,
    )
  } else {
    toastHelpers.success(outcome === 'applied' ? 'Cost set' : 'Cost saved', `${item.name}: ${formatMoney(cost)} per ${item.unit}`)
  }
  return true
}
